//src/components/gro/SurveyRiskSuggestionsTable.tsx
import { useMemo, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Gauge,
  ShieldAlert,
  Sigma,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import type {
  SurveyRiskLevelCode,
  SurveyRiskSuggestionView,
} from "@/types/surveyResults";

type Props = {
  items: SurveyRiskSuggestionView[];
};

type RiskPresentation = {
  text: string;
  badgeClass: string;
  boxClass: string;
  softBoxClass: string;
};

function riskPresentation(level?: SurveyRiskLevelCode | null): RiskPresentation {
  if (level === "critical") {
    return {
      text: "Risco Crítico",
      badgeClass: "border-red-200 bg-red-50 text-red-700",
      boxClass: "border-red-200 bg-red-50/70 text-red-700",
      softBoxClass: "border-red-100 bg-red-50/40",
    };
  }

  if (level === "high") {
    return {
      text: "Risco Alto",
      badgeClass: "border-amber-200 bg-amber-50 text-amber-700",
      boxClass: "border-amber-200 bg-amber-50/70 text-amber-700",
      softBoxClass: "border-amber-100 bg-amber-50/40",
    };
  }

  if (level === "moderate") {
    return {
      text: "Risco Moderado",
      badgeClass: "border-slate-200 bg-slate-100 text-slate-700",
      boxClass: "border-slate-200 bg-slate-100/80 text-slate-700",
      softBoxClass: "border-slate-200 bg-slate-50/70",
    };
  }

  return {
    text: "Risco Baixo",
    badgeClass: "border-emerald-200 bg-emerald-50 text-emerald-700",
    boxClass: "border-emerald-200 bg-emerald-50/70 text-emerald-700",
    softBoxClass: "border-emerald-100 bg-emerald-50/40",
  };
}

function cutTypeLabel(cutType: SurveyRiskSuggestionView["cut_type"]) {
  if (cutType === "unit") return "Unidade";
  if (cutType === "department") return "Departamento";
  if (cutType === "job_position") return "Cargo";
  return "Grupo";
}

function dimensionRiskFromAverage(avg: number) {
  if (avg < 2) return riskPresentation("critical");
  if (avg < 3) return riskPresentation("high");
  if (avg < 4) return riskPresentation("moderate");
  return riskPresentation("low");
}

function formatScore(value?: number | null, digits = 2) {
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return Number(value.toFixed(digits)).toString();
}

function formatResponseScores(values?: number[] | null) {
  if (!values?.length) return "-";
  return values.map((value) => formatScore(value)).join(", ");
}

function toneFromScore(score?: number | null) {
  if (typeof score !== "number") return riskPresentation("low");
  if (score >= 4) return riskPresentation("high");
  if (score === 3) return riskPresentation("moderate");
  return riskPresentation("low");
}

function probabilityRangesTable() {
  return (
    <div className="mt-3 overflow-hidden rounded-lg border border-border/80">
      <div className="grid grid-cols-2 border-b bg-muted/40 text-xs font-medium text-foreground">
        <div className="px-3 py-2">Média da Dimensão</div>
        <div className="px-3 py-2">Probabilidade</div>
      </div>
      {[
        ["≤ 1,4", "1 (Muito Baixa)"],
        ["1,5 a 1,99", "2 (Baixa)"],
        ["2,0 a 2,99", "3 (Média)"],
        ["3,0 a 3,99", "4 (Alta)"],
        ["≥ 4,0", "5 (Muito Alta)"],
      ].map(([range, probability]) => (
        <div key={range} className="grid grid-cols-2 border-b last:border-b-0 text-xs text-muted-foreground">
          <div className="px-3 py-2">{range}</div>
          <div className="px-3 py-2">{probability}</div>
        </div>
      ))}
    </div>
  );
}

function renderAverageFormula(responseScores?: number[] | null, averageScore?: number | null) {
  if (!responseScores?.length) return formatScore(averageScore);
  return `(${responseScores.map((value) => formatScore(value)).join(" + ")}) / ${responseScores.length} = ${formatScore(averageScore)}`;
}

function scoreRangesTable() {
  return (
    <div className="mt-3 overflow-hidden rounded-lg border border-border/80">
      <div className="grid grid-cols-2 border-b bg-muted/40 text-xs font-medium text-foreground">
        <div className="px-3 py-2">Faixa de Score</div>
        <div className="px-3 py-2">Classificação</div>
      </div>
      {[
        ["≤ 5", "Risco Baixo"],
        ["6 a 10", "Risco Moderado"],
        ["11 a 15", "Risco Alto"],
        ["≥ 16", "Risco Crítico"],
      ].map(([range, label]) => (
        <div key={range} className="grid grid-cols-2 border-b last:border-b-0 text-xs text-muted-foreground">
          <div className="px-3 py-2">{range}</div>
          <div className="px-3 py-2">{label}</div>
        </div>
      ))}
    </div>
  );
}

function DetailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border/80 bg-background p-4 shadow-sm">
      <div className="mb-2 text-sm font-semibold text-foreground">{title}</div>
      <div className="text-sm leading-6 text-muted-foreground">{children}</div>
    </div>
  );
}

export function SurveyRiskSuggestionsTable({ items }: Props) {
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const riskNameSort = (a.risk_name || "").localeCompare(
        b.risk_name || "",
        "pt-BR",
        { sensitivity: "base" },
      );
      if (riskNameSort !== 0) return riskNameSort;

      const dimensionSort = (a.dimension_name || a.dimension_code || "").localeCompare(
        b.dimension_name || b.dimension_code || "",
        "pt-BR",
        { sensitivity: "base" },
      );
      if (dimensionSort !== 0) return dimensionSort;

      return (a.cut_name || "").localeCompare(b.cut_name || "", "pt-BR", {
        sensitivity: "base",
      });
    });
  }, [items]);

  function toggleOpen(id: string) {
    setOpenMap((current) => ({
      ...current,
      [id]: !current[id],
    }));
  }

  if (sortedItems.length === 0) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        Nenhuma previsão de risco encontrada.
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {sortedItems.map((item) => {
        const isOpen = !!openMap[item.id];
        const risk = riskPresentation(item.risk_level_code);
        const dimensionRisk = dimensionRiskFromAverage(Number(item.average_score || 0));
        const probabilityTone = toneFromScore(item.probability_score);
        const severityTone = toneFromScore(item.severity_score);
        const calculationSummary = item.calculation_summary;

        return (
          <Collapsible key={item.id} open={isOpen}>
            <Card className="overflow-hidden border-border/80 shadow-sm">
              <div className="p-4">
                <div className="space-y-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="text-base font-semibold">
                          Risco: {item.risk_name || "Risco sugerido"}
                        </div>

                        <Badge variant="outline" className={risk.badgeClass}>
                          {risk.text}
                        </Badge>
                      </div>

                      <div className="text-sm text-muted-foreground">
                        Dimensão: {item.dimension_name || item.dimension_code || "Dimensão"} • {cutTypeLabel(item.cut_type)}: {item.cut_name}
                      </div>
                    </div>
                  </div>

                  <div className={`rounded-xl border px-4 py-3 ${risk.softBoxClass}`}>
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Leitura simples do risco
                    </div>
                    <div className="mt-1 text-sm leading-6 text-foreground/80">
                      {item.user_summary || "Sem resumo disponível."}
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <div className={`rounded-xl border p-4 ${dimensionRisk.boxClass}`}>
                      <div className="mb-3 flex items-center justify-between">
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Média da dimensão
                        </div>
                        <Gauge className="h-4 w-4" />
                      </div>

                      <div className="text-2xl font-semibold">
                        {formatScore(item.average_score)}
                      </div>

                      <div className="mt-1 text-xs font-medium">
                        {dimensionRisk.text}
                      </div>

                      <div className="mt-2 text-xs text-muted-foreground">
                        A média mostra como está o ambiente no recorte analisado.
                      </div>
                    </div>

                    <div className={`rounded-xl border p-4 ${probabilityTone.boxClass}`}>
                      <div className="mb-3 flex items-center justify-between">
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Probabilidade
                        </div>
                        <AlertTriangle className="h-4 w-4" />
                      </div>

                      <div className="text-2xl font-semibold leading-tight">
                        {item.probability_label || "-"} {item.probability_score != null ? `(${item.probability_score})` : ""}
                      </div>

                      <div className="mt-1 text-xs font-medium">
                        {probabilityTone.text}
                      </div>

                      <div className="mt-2 text-xs text-muted-foreground">
                        Chance estimada de o problema gerar risco real.
                      </div>
                    </div>

                    <div className={`rounded-xl border p-4 ${severityTone.boxClass}`}>
                      <div className="mb-3 flex items-center justify-between">
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Severidade
                        </div>
                        <ShieldAlert className="h-4 w-4" />
                      </div>

                      <div className="text-2xl font-semibold leading-tight">
                        {item.severity_label || "-"} {item.severity_score != null ? `(${item.severity_score})` : ""}
                      </div>

                      <div className="mt-1 text-xs font-medium">
                        {severityTone.text}
                      </div>

                      <div className="mt-2 text-xs text-muted-foreground">
                        Impacto esperado caso esse risco aconteça.
                      </div>
                    </div>

                    <div className={`rounded-xl border p-4 ${risk.boxClass}`}>
                      <div className="mb-3 flex items-center justify-between">
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Score final
                        </div>
                        <Sigma className="h-4 w-4" />
                      </div>

                      <div className="text-3xl font-semibold leading-none">
                        {formatScore(item.risk_score)} <span className="text-base font-medium text-muted-foreground">de 25</span>
                      </div>

                      <div className="mt-2 text-xs font-medium">
                        {item.risk_level_name || risk.text}
                      </div>

                      <div className="mt-2 text-xs text-muted-foreground">
                        Probabilidade × Severidade / máximo 25.
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end border-t border-dashed pt-3">
                    <button
                      type="button"
                      onClick={() => toggleOpen(item.id)}
                      className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                    >
                      {isOpen ? (
                        <>
                          Ocultar detalhes
                          <ChevronUp className="h-4 w-4" />
                        </>
                      ) : (
                        <>
                          Ver detalhes
                          <ChevronDown className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <CollapsibleContent>
                <div className="border-t bg-muted/20 p-4">
                  <div className="grid gap-3 xl:grid-cols-2">
                    <DetailSection title="Explicação da média da dimensão">
                      {item.average_explanation || "Sem explicação."}
                    </DetailSection>

                    <DetailSection title="Explicação da probabilidade">
                      <div>{item.probability_explanation || "Sem explicação."}</div>
                      {item.probability_source !== "fixed" ? probabilityRangesTable() : null}
                    </DetailSection>

                    <DetailSection title="Explicação da severidade">
                      {item.severity_explanation || "Sem explicação."}
                    </DetailSection>

                    <DetailSection title="Explicação do score final">
                      <div>{item.score_explanation || "Sem explicação."}</div>
                      {scoreRangesTable()}
                    </DetailSection>

                    <DetailSection title="Como o sistema calculou">
                      <div className="space-y-1.5">
                        <div>
                          <strong>Respondentes:</strong> {calculationSummary?.respondents ?? item.respondents_count}
                        </div>
                        <div>
                          <strong>Respostas:</strong> ({formatResponseScores(calculationSummary?.response_scores || item.response_scores)})
                        </div>
                        <div>
                          <strong>Média da Dimensão:</strong> {renderAverageFormula(
                            calculationSummary?.response_scores || item.response_scores,
                            calculationSummary?.average_score ?? item.average_score,
                          )}
                        </div>
                        <div>
                          <strong>Cálculo do score:</strong> {calculationSummary?.score_formula || `${item.probability_score ?? "-"} x ${item.severity_score ?? "-"} = ${formatScore(item.risk_score)}`}
                        </div>
                      </div>
                    </DetailSection>

                    <DetailSection title="Possível dano">
                      {item.possible_damage || "Não informado."}
                    </DetailSection>

                    <DetailSection title="Descrição técnica do risco">
                      {item.risk_description || "Não informada."}
                    </DetailSection>

                    <DetailSection title="Diretrizes iniciais de controle">
                      {item.control_guidelines || "Não informado."}
                    </DetailSection>
                  </div>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        );
      })}
    </div>
  );
}
